<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { almEventsWidgetEventOpened } from "@/analytics/events";
import { trackEvent } from "@/analytics/track";
import { refreshAlmEvents, useAlmEvents } from "@/composables/useAlmEvents";
import { AlmConfigurationHandler } from "@/handlers/almConfigurationHandler";
import EventService from "@/services/event.service";
import { createHandlerContext } from "@/stores/context";
import { compactRelativeTime } from "@/utils/dateFormat";

import BaseSpinner from "../BaseSpinner.vue";
import SvgIcon from "../SvgIcon.vue";
import WidgetCard from "./WidgetCard.vue";

const MAX_VISIBLE = 20;

const router = useRouter();

const connections = ref([]);
const connectionsLoading = ref(true);
const connectionsError = ref(false);

async function loadConnections() {
  connectionsLoading.value = true;
  connectionsError.value = false;
  try {
    const data = await new AlmConfigurationHandler(createHandlerContext()).getAlmConnections();
    connections.value = data || [];
  } catch (e) {
    connectionsError.value = true;
    throw e;
  } finally {
    connectionsLoading.value = false;
  }
}

onMounted(loadConnections);

const { events: allEvents, isLoading, error: eventsError } = useAlmEvents();

const loading = computed(() => connectionsLoading.value || isLoading.value);
const loadError = computed(() => connectionsError.value || !!eventsError.value);

async function fetchAllEvents() {
  if (!connections.value.length) return [];
  const results = await Promise.allSettled(
    connections.value.map(async (connection) => {
      const list = await EventService.listEvents(connection.id, MAX_VISIBLE);
      return (list || []).map((ev) => ({
        ...ev,
        _connectionId: connection.id,
        _connectionName: connection.name?.trim() || "(unnamed)",
        _almType: connection.alm_type,
      }));
    }),
  );
  const fulfilled = results.filter((r) => r.status === "fulfilled");
  if (fulfilled.length === 0) {
    throw new Error("All ALM event fetches failed");
  }
  return fulfilled
    .flatMap((r) => r.value)
    .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
}

function triggerRefresh(force = false) {
  refreshAlmEvents(fetchAllEvents, { force });
}

watch(
  () => connections.value.length,
  (count) => {
    if (count > 0) triggerRefresh(false);
  },
  { immediate: true },
);

const events = computed(() => (allEvents.value || []).slice(0, MAX_VISIBLE));

const ERROR_RE =
  /(fail|error|could not|not authorized|unauthorized|forbidden|timeout|refused|401|403|404|500|502|503|504)/i;

function isError(event) {
  const desc = event.description || "";
  const msg = event.message || "";
  return ERROR_RE.test(desc) || ERROR_RE.test(msg);
}

function openEvent(event) {
  trackEvent(almEventsWidgetEventOpened());
  router.push({
    name: "almConnectionEvents",
    params: { connectionId: event._connectionId, almType: event._almType, eventId: event.id },
  });
}

function onRefresh() {
  triggerRefresh(true);
}
</script>

<template>
  <WidgetCard class="alm-events-widget" icon="system/refresh">
    <template #title>Recent ALM events</template>
    <template #actions>
      <button
        type="button"
        class="refresh"
        :class="{ spinning: isLoading }"
        :disabled="isLoading || !connections.length"
        aria-label="Refresh ALM events"
        @click="onRefresh"
      >
        <SvgIcon name="system/refresh" width="16" height="16" />
      </button>
    </template>

    <ul v-if="events.length" class="list">
      <li v-for="event in events" :key="`${event._connectionId}-${event.id}`" class="row-item">
        <button type="button" class="row" @click="openEvent(event)">
          <span class="severity-dot" :class="{ error: isError(event) }" aria-hidden="true" />
          <span class="description">{{ event.description || "Event" }}</span>
          <span class="connection">{{ event._connectionName }}</span>
          <span class="time">{{ compactRelativeTime(event.created) }}</span>
        </button>
      </li>
    </ul>

    <div v-else-if="loading" class="loading">
      <BaseSpinner delayed size="20" />
    </div>

    <div v-else-if="loadError" class="empty">
      <p class="empty-copy">Something went wrong while loading ALM events.</p>
    </div>

    <div v-else class="empty">
      <p class="empty-copy">
        {{
          connections.length
            ? "No events recorded for your ALM connections yet."
            : "Connect an ALM tool to see events here."
        }}
      </p>
    </div>
  </WidgetCard>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/palette" as p;
@use "@/styles/mixins/typography";

.refresh {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid c.$border-subtle;
  background: c.$background-ui;
  color: c.$text-subtle;
  cursor: pointer;
  flex-shrink: 0;
}

.refresh:hover:not(:disabled) {
  color: c.$main;
  background: p.$black-alpha-5;
}

.refresh:disabled {
  cursor: progress;
  opacity: 0.6;
}

.refresh.spinning :deep(svg) {
  animation: spin 800ms linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.list {
  list-style: none;
  margin: 0;
  padding: 6px 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.row-item {
  display: flex;
  align-items: center;
}

.row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) 120px 56px;
  align-items: center;
  gap: 10px;
  flex: 1;
  text-align: left;
  appearance: none;
  background: none;
  border: none;
  padding: 6px 12px;
  font-family: inherit;
  cursor: pointer;
  border-radius: 6px;
  min-height: 32px;
}

.row:hover {
  background: p.$black-alpha-5;
}

.row:hover .description {
  color: c.$main-hover;
}

.severity-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: c.$grey-light;
  flex-shrink: 0;

  &.error {
    background: c.$red;
  }
}

.description {
  @include typography.small("regular");

  color: c.$text-subtle;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  margin-right: 24px;
}

.row:hover .description {
  color: c.$main;
}

.connection {
  @include typography.label("medium");

  color: c.$text-subtle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.time {
  @include typography.label("regular");

  color: c.$text-subtle;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.loading {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
}

.empty {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.empty-copy {
  @include typography.small;

  color: c.$text-subtle;
  margin: 0;
  text-align: center;
}
</style>
